import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { ThemeProvider } from './components/theme/ThemeContext';
import { TestModeProvider } from './components/testMode/TestModeContext';
import { ConfirmProvider } from './components/ui/ConfirmProvider';
import { I18nProvider } from './contexts/I18nContext';
import { queryClient } from './lib/queryClient';
import { registerServiceWorker } from './lib/pwa';
import { armSounds } from './lib/sounds';
import { listenForPushSounds } from './lib/pushSounds';
import { initViewportTracking } from './lib/viewport';
import './index.css';

// Mirror the keyboard-aware visual viewport into CSS custom properties before
// first paint so mobile layout (CLE-114) collapses correctly when the iOS
// keyboard opens.
initViewportTracking();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <ThemeProvider>
      <I18nProvider>
        <QueryClientProvider client={queryClient}>
          <TestModeProvider>
            <ConfirmProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </ConfirmProvider>
          </TestModeProvider>
        </QueryClientProvider>
      </I18nProvider>
    </ThemeProvider>
  </StrictMode>,
);

// Register the PWA service worker (no-op in dev / unsupported browsers).
registerServiceWorker();

// Browsers keep an AudioContext suspended until the person interacts with the
// page, and a suspended context plays nothing while reporting success. This
// listens for the first click or key press and resumes it, so the first
// message of a session is actually heard.
armSounds();
listenForPushSounds();
